import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit3, Users, Layers, Clock, Globe, Instagram, Twitter, Youtube, Linkedin, Facebook } from 'lucide-react';
import { V, cardStyle, cardHoverStyle, secondaryBtnStyle, labelStyle, avatarGradient, formatRelative } from '../vividGlass';

const SOCIAL_ICONS = {
  instagram: Instagram, twitter: Twitter, x: Twitter,
  youtube: Youtube, linkedin: Linkedin, facebook: Facebook,
};

function CollabCircles({ collaborators, isOwner, onManageClick }) {
  const visible = collaborators.slice(0, 4);
  const extra = collaborators.length - 4;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {visible.map((c, i) => (
        <div key={c.collaborator_id} title={c.client_name} style={{
          width: 30, height: 30, borderRadius: '50%',
          background: avatarGradient(c.client_name || ''),
          border: '2px solid #050505',
          marginLeft: i > 0 ? -10 : 0, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, color: '#fff', fontFamily: V.font,
        }}>
          {(c.client_name || '?').charAt(0).toUpperCase()}
        </div>
      ))}
      {extra > 0 && (
        <div title={`+${extra} more`} style={{
          width: 30, height: 30, borderRadius: '50%',
          background: V.glass, border: `2px solid #050505`,
          marginLeft: -10, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700, color: V.textSub, fontFamily: V.font,
        }}>
          +{extra}
        </div>
      )}
      {isOwner && (
        <motion.div
          title="Manage collaborators"
          whileHover={{ background: 'rgba(79,58,125,0.35)' }}
          whileTap={{ scale: 0.92 }}
          onClick={onManageClick}
          style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            marginLeft: collaborators.length > 0 ? -10 : 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(79,58,125,0.15)',
            border: '2px dashed rgba(79,58,125,0.6)',
            cursor: 'pointer',
          }}
        >
          <Users size={11} color="#C4B5FD" />
        </motion.div>
      )}
    </div>
  );
}

function GhostBtn({ icon, label, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.button whileTap={{ scale: 0.96 }} onClick={onClick}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', borderRadius: 12,
        background: hovered ? 'rgba(255,255,255,0.08)' : 'transparent',
        border: `1px solid ${hovered ? V.glassBorderHover : 'rgba(255,255,255,0.10)'}`,
        color: hovered ? V.text : V.textSub,
        fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: V.font,
        transition: 'all 0.15s',
      }}>
      {icon}{label}
    </motion.button>
  );
}

export default function BrandInfoCard({ brand, brandId, onEditClick, onCollaboratorsClick, onNavigate }) {
  const [hovered, setHovered] = useState(false);
  const collaborators = brand.collaborators || [];
  const social = Array.isArray(brand.social_media_urls) ? brand.social_media_urls.filter((s) => s?.url) : [];
  const score = brand.completion_score || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...cardStyle,
        ...(hovered ? cardHoverStyle : {}),
        marginBottom: 24,
      }}
    >
      {/* Identity row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 18 }}>
        {/* Logo / avatar */}
        {brand.logo_url ? (
          <img src={brand.logo_url} alt={brand.brand_name}
            style={{ width: 72, height: 72, borderRadius: 18, flexShrink: 0, objectFit: 'contain', background: 'rgba(255,255,255,0.04)', border: `1px solid ${V.glassBorder}` }}
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div style={{
          width: 72, height: 72, borderRadius: 18, flexShrink: 0,
          background: avatarGradient(brand.brand_name || ''),
          display: brand.logo_url ? 'none' : 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 800, color: '#fff', fontFamily: V.font,
        }}>
          {(brand.brand_name || '?').charAt(0).toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* H1 — 32px bold white */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <h1 style={{ fontSize: 32, fontWeight: 700, color: V.text, margin: 0, lineHeight: 1.1, fontFamily: V.font }}>
              {brand.brand_name}
            </h1>
            {brand.is_default && (
              <span style={{ padding: '3px 10px', borderRadius: 99, background: 'rgba(255,210,34,0.12)', border: '1px solid rgba(255,210,34,0.3)', color: V.warning, fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                Default
              </span>
            )}
            {!brand.is_owner && (
              <span style={{ padding: '3px 10px', borderRadius: 99, background: 'rgba(79,58,125,0.2)', border: '1px solid rgba(79,58,125,0.5)', color: '#C4B5FD', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                Shared
              </span>
            )}
          </div>
          {/* Tagline — 14px body #94A3B8 */}
          {brand.tagline && (
            <p style={{ fontSize: 14, color: V.textSub, margin: '0 0 10px', fontStyle: 'italic', fontFamily: V.font }}>
              &ldquo;{brand.tagline}&rdquo;
            </p>
          )}
          {/* Meta chips */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {brand.industry && (
              <span style={{ padding: '3px 10px', borderRadius: 99, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 12, fontFamily: V.font }}>
                {brand.industry}
              </span>
            )}
            {brand.brand_language && (
              <span style={{ padding: '3px 10px', borderRadius: 99, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 12, fontFamily: V.font }}>
                {brand.brand_language}
              </span>
            )}
            {brand.use_emojis ? (
              <span style={{ padding: '3px 10px', borderRadius: 99, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 12, fontFamily: V.font }}>
                Emoji-friendly
              </span>
            ) : null}
          </div>
        </div>

        {/* DNA Strength — top right */}
        <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 88 }}>
          <span style={{ ...labelStyle, marginBottom: 6 }}>DNA</span>
          {score >= 100 ? (
            <span style={{ padding: '4px 12px', borderRadius: 99, background: 'rgba(255,210,34,0.12)', border: '1px solid rgba(255,210,34,0.3)', color: V.warning, fontSize: 11, fontWeight: 700, fontFamily: V.font }}>
              ✦ Complete
            </span>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: V.warning, fontWeight: 700, fontFamily: V.font }}>{score}%</span>
              </div>
              {/* h-1.5 track per spec */}
              <div style={{ height: 6, width: 88, background: 'rgba(0,0,0,0.4)', borderRadius: 99, overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${score}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{ height: '100%', background: V.warning, borderRadius: 99, boxShadow: '0 0 10px #FFD222' }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Social links */}
      {(social.length > 0 || brand.source_url) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {social.map((s, i) => {
            const Icon = SOCIAL_ICONS[s.platform?.toLowerCase()] || Globe;
            return (
              <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 10, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 12, textDecoration: 'none', fontFamily: V.font }}>
                <Icon size={13} /><span style={{ textTransform: 'capitalize' }}>{s.platform}</span>
              </a>
            );
          })}
          {brand.source_url && (
            <a href={brand.source_url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 10, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 12, textDecoration: 'none', fontFamily: V.font }}>
              <Globe size={13} />Website
            </a>
          )}
        </div>
      )}

      {/* Footer: collaborators + date + action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, paddingTop: 16, borderTop: `1px solid ${V.glassBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CollabCircles collaborators={collaborators} isOwner={brand.is_owner} onManageClick={onCollaboratorsClick} />
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: V.textMuted, fontSize: 11, fontFamily: V.font }}>
            <Clock size={10} />
            {formatRelative(brand.updated_at || brand.created_at)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {brand.is_owner && (
            <GhostBtn icon={<Edit3 size={13} />} label="Edit Brand" onClick={onEditClick} />
          )}
          {/* Secondary purple buttons per design system */}
          <motion.button whileTap={{ scale: 0.96 }} onClick={onCollaboratorsClick}
            style={{ ...secondaryBtnStyle, padding: '8px 14px', fontSize: 13 }}>
            <Users size={13} />Collaborators
          </motion.button>
          <motion.button whileTap={{ scale: 0.96 }} onClick={() => onNavigate(`/brand-dna/${brandId}/assets`)}
            style={{ ...secondaryBtnStyle, padding: '8px 14px', fontSize: 13 }}>
            <Layers size={13} />Assets
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}
